import { priorityName, type Label, type Member } from '@hive/shared';
import { Plus } from 'lucide-react';
import { forwardRef, useState } from 'react';
import { Input } from '@/components/ui/field';
import { planQuickAdd, type QuickAddPlan } from './model';
import { LabelChip } from './TaskBits';

/** "Fix jump buffer p1 @jono #combat tomorrow", then Enter. Shows what it understood as you type. */
export const QuickAdd = forwardRef<
  HTMLInputElement,
  {
    members: readonly Member[];
    labels: readonly Label[];
    disabled?: boolean;
    onAdd: (plan: QuickAddPlan) => Promise<void>;
  }
>(function QuickAdd({ members, labels, disabled, onAdd }, ref) {
  const [text, setText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const plan = text.trim() ? planQuickAdd(text, members, labels) : null;

  async function submit() {
    if (!plan?.title || saving) return;
    setSaving(true);
    setError(null);
    try {
      await onAdd(plan);
      setText('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not add the task.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center gap-2">
        <Plus className="size-4 shrink-0 text-cyan" aria-hidden />
        <Input
          ref={ref}
          value={text}
          disabled={disabled || saving}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              void submit();
            } else if (e.key === 'Escape') {
              e.stopPropagation();
              setText('');
              e.currentTarget.blur();
            }
          }}
          placeholder="New task: Fix jump buffer p1 @jono #combat tomorrow  (press n)"
          aria-label="New task"
        />
      </div>
      {plan ? (
        <p
          className="flex flex-wrap items-center gap-2 pl-6 text-xs text-muted"
          aria-live="polite"
          data-testid="quick-add-preview"
        >
          <span className="text-text">{plan.title || <em className="text-red">needs a title</em>}</span>
          {plan.priority ? (
            <span className="font-mono">
              P{plan.priority} {priorityName(plan.priority)}
            </span>
          ) : null}
          {plan.assignee ? <span className="font-mono text-green">@{plan.assignee.display_name}</span> : null}
          {plan.unknownAssignee ? (
            <span className="font-mono text-red">@{plan.unknownAssignee}? nobody by that name</span>
          ) : null}
          {plan.labels.map((l) => (
            <LabelChip key={l.id} label={l} />
          ))}
          {plan.newLabels.map((name) => (
            <span key={name} className="font-mono text-magenta">
              #{name} (new)
            </span>
          ))}
          {plan.due ? <span className="font-mono text-yellow">due {plan.due}</span> : null}
        </p>
      ) : null}
      {error ? (
        <p role="alert" className="pl-6 text-xs text-red">
          {error}
        </p>
      ) : null}
    </div>
  );
});
